import { getBrandPath } from "../contexts/ConfigContext/constants";

const STALL_PATHS = ["/hikiniku"];
const PATH_UUID_PATTERN = /^\/uuid=([a-zA-Z0-9-]+)/;

const getViteBaseUrl = (): string => import.meta.env.BASE_URL || "/";

const redirectPathBasedUuid = (pathUuid: string, viteBaseUrl: string, search: string): void => {
	const newSearchParams = new URLSearchParams(search);

	newSearchParams.set("uuid", pathUuid);

	const qs = newSearchParams.toString();

	if (viteBaseUrl === "/") {
		// Same-origin redirect: query string is sanitised through URLSearchParams.
		globalThis.location.replace(`/?${qs}`); // NOSONAR tssecurity:S6105

		return;
	}

	// Same-origin redirect: base from Vite, query sanitised through URLSearchParams.
	const base = viteBaseUrl.endsWith("/") ? viteBaseUrl.slice(0, -1) : viteBaseUrl;

	globalThis.location.replace(`${base}/index.html?${qs}`); // NOSONAR tssecurity:S6105
};

const redirectToBrandPath = (brandPath: string, search: string): void => {
	// brandPath is from getBrandPath()'s closed allowlist; redirect stays same-origin.
	// Trailing slash is required so the resulting URL matches BrowserRouter's basename
	// cleanly when the SPA reloads (e.g. /hikiniku/?uuid=... — basename `/hikiniku`,
	// next char `/`). Without it, deep-link refresh on the brand path renders black
	// because some downstream handlers expect the canonical trailing-slash form.
	globalThis.location.replace(`${brandPath}/${search}`); // NOSONAR tssecurity:S6105
};

const findStallBasePath = (pathname: string): string | null => {
	for (const stallPath of STALL_PATHS) {
		if (pathname.includes(stallPath)) {
			const baseIndex = pathname.indexOf(stallPath);

			return pathname.substring(0, baseIndex) + stallPath;
		}
	}

	return null;
};

// Dynamic base URL detection for multi-stall support
const getPublicBaseUrl = (): string => {
	if (typeof globalThis === "undefined" || !globalThis.location) {
		return getViteBaseUrl();
	}

	const { pathname, search } = globalThis.location;
	const urlParams = new URLSearchParams(search);
	const uuid = urlParams.get("uuid");

	// Handle path-based UUID format: /uuid=<uuid>
	// Some QR codes encode uuid in the path instead of query string.
	const pathUuidMatch = PATH_UUID_PATTERN.exec(pathname);

	if (pathUuidMatch) {
		const viteBaseUrl = getViteBaseUrl();

		redirectPathBasedUuid(pathUuidMatch[1], viteBaseUrl, search);

		return viteBaseUrl;
	}

	// Handle UUID-based auto-redirect to brand base path at root.
	// e.g. /?uuid=<hikinikusUUID> -> /hikiniku?uuid=<...>
	if (pathname === "/" && uuid) {
		const brandPath = getBrandPath(uuid);

		if (brandPath) {
			redirectToBrandPath(brandPath, search);

			return brandPath;
		}
	}

	// Check for existing multi-stall paths
	const stallBase = findStallBasePath(pathname);

	if (stallBase) {
		return stallBase;
	}

	return getViteBaseUrl();
};

export const publicBaseUrl = getPublicBaseUrl();
export const GIT_COMMIT = import.meta.env.VITE_GIT_COMMIT;

// Version Display related constants
export const APP_VERSION = import.meta.env.VITE_APP_VERSION;
export const GIT_COMMIT_HASH = import.meta.env.VITE_GIT_COMMIT;
export const APP_MODE = import.meta.env.MODE;
export const DD_ENV = import.meta.env.VITE_DD_ENV;
